import { SideModal } from "shared";
import SVG from "shared/SVG";
import { SVG_ENUM } from "enums";
import { useState } from "react";
import "./ArtworkDetailsSideModal.scss";

interface ArtworkDetailsSideModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
}

const ArtworkDetailsSideModal = ({
  isOpen,
  onClose,
  item,
}: ArtworkDetailsSideModalProps) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const photoFiles = (item?.files ?? []).filter((f: any) => f.role === "photo");

  const contractFiles = (item?.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const handleDownloadAll = () => {
    if (!item) return;
    contractFiles.forEach((file: any) => {
      const link = document.createElement("a");
      link.href = file.file.url;
      link.download = file.file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) =>
      prev === 0 ? photoFiles.length - 1 : prev - 1,
    );
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) =>
      prev === photoFiles.length - 1 ? 0 : prev + 1,
    );
  };

  const formatPrice = () => {
    if (!item?.primaryValue) return "Brak";
    const formatted = Number(item.primaryValue).toLocaleString("pl-PL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const currency = item.currency?.currency ?? "";
    return currency ? `${formatted} ${currency}` : formatted;
  };

  return (
    <SideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Szczegółowe informacje"
      subtitle="Aktywo: Dzieło sztuki"
      className="side-modal--artwork-details"
    >
      <div className="artwork-details">
        <div className="artwork-details__price-header">
          Cena zakupu: <span>{formatPrice()}</span>
        </div>

        <div className="artwork-details__field">
          <span className="artwork-details__label">Rodzaj</span>
          <span className="artwork-details__value">
            {item?.details?.artworkType || "Brak"}
          </span>
        </div>

        <div className="artwork-details__field">
          <span className="artwork-details__label">Cena zakupu</span>
          <span className="artwork-details__value">{formatPrice()}</span>
        </div>

        <div className="artwork-details__field">
          <span className="artwork-details__label">Waluta</span>
          <span className="artwork-details__value">
            {item?.currency?.currency || "Brak"}
          </span>
        </div>

        <div className="artwork-details__field">
          <span className="artwork-details__label">Tytuł</span>
          <span className="artwork-details__value">{item?.name || "Brak"}</span>
        </div>

        <div className="artwork-details__field">
          <span className="artwork-details__label">Autor</span>
          <span className="artwork-details__value">
            {item?.details?.author || "Brak"}
          </span>
        </div>

        <div className="artwork-details__field">
          <span className="artwork-details__label">Epoka/wiek</span>
          <span className="artwork-details__value">
            {item?.details?.eraAge || "Brak"}
          </span>
        </div>

        <div className="artwork-details__field">
          <span className="artwork-details__label">Dodatkowe informacje</span>
          <span className="artwork-details__value">
            {item?.additionalInfo || "Brak"}
          </span>
        </div>

        <div className="artwork-details__field artwork-details__field--photos">
          <span className="artwork-details__label">
            Załączone zdjęcia ({photoFiles.length})
          </span>
          {photoFiles.length > 0 ? (
            <div className="artwork-details__carousel">
              <div className="artwork-details__carousel-image-wrapper">
                <img
                  src={photoFiles[currentPhotoIndex]?.file?.url}
                  alt={`Zdjęcie ${currentPhotoIndex + 1}`}
                  className="artwork-details__carousel-image"
                />

                {photoFiles.length > 1 && (
                  <>
                    <button
                      className="artwork-details__carousel-arrow artwork-details__carousel-arrow--prev"
                      onClick={handlePrevPhoto}
                    >
                      <svg
                        width="5"
                        height="8"
                        viewBox="0 0 5 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.87695 0.779297L0.778561 3.91127L3.87695 7.04325"
                          stroke="#151515"
                          strokeWidth="1.55757"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <button
                      className="artwork-details__carousel-arrow artwork-details__carousel-arrow--next"
                      onClick={handleNextPhoto}
                    >
                      <svg
                        width="5"
                        height="8"
                        viewBox="0 0 5 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.87695 0.779297L0.778561 3.91127L3.87695 7.04325"
                          stroke="#151515"
                          strokeWidth="1.55757"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {photoFiles.length > 1 && (
                <div className="artwork-details__carousel-dots">
                  {photoFiles.map((_: any, index: number) => (
                    <button
                      key={index}
                      className={`artwork-details__carousel-dot ${
                        index === currentPhotoIndex
                          ? "artwork-details__carousel-dot--active"
                          : ""
                      }`}
                      onClick={() => setCurrentPhotoIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="artwork-details__value">Brak</span>
          )}
        </div>

        <div className="artwork-details__field artwork-details__field--files">
          <div className="artwork-details__files-header">
            <span className="artwork-details__label">Umowa</span>
            {contractFiles.length > 0 && (
              <button
                className="artwork-details__download-all"
                onClick={handleDownloadAll}
              >
                Pobierz wszystko
                <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
              </button>
            )}
          </div>
          {contractFiles.length === 0 ? (
            <span className="artwork-details__value">Brak</span>
          ) : (
            <div className="artwork-details__files-list">
              {contractFiles.map((file: any) => (
                <div
                  key={file.file.uuid}
                  className="artwork-details__file-item"
                >
                  <span className="artwork-details__file-name">
                    {file.file.name}
                  </span>
                  <a
                    href={file.file.url}
                    download={file.file.name}
                    target="_blank"
                    rel="noreferrer"
                    className="artwork-details__file-download"
                  >
                    <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default ArtworkDetailsSideModal;
